import React from "react";

function hexToRgb(hex) {
  const normalized = String(hex || "").replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return { r: 88, g: 101, b: 242 };
  }

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

const ARTWORK_BLEED = 10;
const MIN_LAYER_SIZE = 5;
const MAX_LAYER_SIZE = 120;
const MIN_VISIBLE = 5;
const DETAIL_REFERENCE_WIDTH = 420;
const DETAIL_REFERENCE_HEIGHT = 560;

function clampRange(value, min, max, fallback) {
  const parsed = Number(value);
  const next = Number.isFinite(parsed) ? parsed : fallback;
  return Math.min(max, Math.max(min, next));
}

function clampOpacity(value, fallback = 1) {
  const next = Number(value);
  if (!Number.isFinite(next)) return fallback;
  return Math.min(1, Math.max(0, next));
}

function getDetailDimension(percent, referenceSize) {
  const pixels = (percent / 100) * referenceSize;
  return `min(${percent}%, ${pixels}px)`;
}

function getDetailPosition(position, size, referenceSize, startProperty, endProperty) {
  const useStartEdge = position + size / 2 <= 50;
  const offset = useStartEdge ? position : 100 - position - size;
  const pixels = (offset / 100) * referenceSize;
  const responsiveOffset = offset < 0
    ? `max(${offset}%, ${pixels}px)`
    : `min(${offset}%, ${pixels}px)`;

  return useStartEdge
    ? { [startProperty]: responsiveOffset, [endProperty]: "auto" }
    : { [startProperty]: "auto", [endProperty]: responsiveOffset };
}

export function getProductEffectStyle(product) {
  if (!product?.effect) return {};

  const effect = product.effect;
  const glow = hexToRgb(effect.glowColor || effect.borderColor);
  const glowOpacity = Number(effect.glowOpacity) || 0;

  return {
    border: `${Number(effect.borderWidth) || 0}px solid ${effect.borderColor || "#5865F2"}`,
    borderRadius: `${Number(effect.radius) || 0}px`,
    boxShadow: `0 0 ${Number(effect.glowBlur) || 0}px rgba(${glow.r}, ${glow.g}, ${glow.b}, ${glowOpacity})`,
    overflow: "visible",
  };
}

export function ShopProductArtworkOverlay({ product, displayMode = "card" }) {
  const artwork = product?.artwork;
  const layers = Array.isArray(artwork?.layers) ? artwork.layers : [];
  if (!artwork?.enabled || (!artwork.mediaSrc && !artwork.imageData && layers.length === 0)) {
    return null;
  }

  const legacyLayers =
    layers.length > 0 || !artwork.mediaSrc
      ? layers
      : [
          {
            id: "legacy-media",
            src: artwork.mediaSrc,
            x: artwork.x,
            y: artwork.y,
            width: artwork.width,
            height: artwork.height,
            radius: artwork.radius,
            fit: artwork.fit,
            blendMode: artwork.blendMode,
            opacity: artwork.mediaOpacity,
          },
        ];

  return (
    <div className="absolute inset-0 pointer-events-none z-20 overflow-visible" aria-hidden="true">
      {artwork.imageData && (
        <img
          src={artwork.imageData}
          alt=""
          className="absolute"
          style={{
            left: artwork.drawingBleed ? `-${ARTWORK_BLEED}%` : 0,
            top: artwork.drawingBleed ? `-${ARTWORK_BLEED}%` : 0,
            width: artwork.drawingBleed ? `${100 + ARTWORK_BLEED * 2}%` : "100%",
            height: artwork.drawingBleed ? `${100 + ARTWORK_BLEED * 2}%` : "100%",
            objectFit: "fill",
            opacity: clampOpacity(artwork.drawingOpacity),
          }}
        />
      )}
      {legacyLayers.map((layer, index) => {
        if (!layer?.src) return null;
        const width = clampRange(layer.width, MIN_LAYER_SIZE, MAX_LAYER_SIZE, 30);
        const height = clampRange(layer.height, MIN_LAYER_SIZE, MAX_LAYER_SIZE, 30);
        const minX = Math.max(-ARTWORK_BLEED, MIN_VISIBLE - width);
        const maxX = Math.min(100 - MIN_VISIBLE, 100 + ARTWORK_BLEED - width);
        const minY = Math.max(-ARTWORK_BLEED, MIN_VISIBLE - height);
        const maxY = Math.min(100 - MIN_VISIBLE, 100 + ARTWORK_BLEED - height);
        const x = clampRange(layer.x, minX, maxX, 10);
        const y = clampRange(layer.y, minY, maxY, 10);
        const fit = layer.fit === "fill" ? "fill" : layer.fit === "cover" ? "cover" : "contain";
        const preserveAspectRatio = layer.preserveAspectRatio === true;
        const blendMode = ["screen", "lighten", "multiply", "overlay"].includes(layer.blendMode)
          ? layer.blendMode
          : "normal";
        const isDetail = displayMode === "detail";
        const detailPosition = isDetail
          ? {
              ...getDetailPosition(x, width, DETAIL_REFERENCE_WIDTH, "left", "right"),
              ...getDetailPosition(y, height, DETAIL_REFERENCE_HEIGHT, "top", "bottom"),
            }
          : { left: `${x}%`, top: `${y}%` };

        return (
          <img
            key={layer.id || `${layer.src}-${index}`}
            src={layer.src}
            alt=""
            className="absolute"
            style={{
              ...detailPosition,
              width: isDetail ? getDetailDimension(width, DETAIL_REFERENCE_WIDTH) : `${width}%`,
              height: preserveAspectRatio
                ? "auto"
                : isDetail
                  ? getDetailDimension(height, DETAIL_REFERENCE_HEIGHT)
                  : `${height}%`,
              borderRadius: `${Number(layer.radius) || 0}px`,
              objectFit: preserveAspectRatio ? "contain" : fit,
              mixBlendMode: blendMode,
              opacity: clampOpacity(layer.opacity),
              transform: `rotate(${Number(layer.rotation) || 0}deg)`,
              transformOrigin: "center center",
            }}
          />
        );
      })}
    </div>
  );
}

export function ShopProductFrame({
  product,
  children,
  className = "",
  style = {},
  artworkMode = "card",
  contentStyle = {},
  ...rest
}) {
  const effectStyle = getProductEffectStyle(product);
  return (
    <div className={`relative isolate ${className}`} style={{ ...effectStyle, ...style, overflow: "visible" }} {...rest}>
      <div
        className="relative flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden"
        style={{ borderRadius: "inherit", ...contentStyle }}
      >
        {children}
      </div>
      <ShopProductArtworkOverlay product={product} displayMode={artworkMode} />
    </div>
  );
}
